// src/components/message-board/MessageComposer.js
import React, { useState, useRef } from 'react';
import { Send, X, Image as ImageIcon, Loader, Palette } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import DrawingCanvas from './DrawingCanvas';

const MessageComposer = ({ 
  leagueId,
  onSubmit, 
  onCancel, 
  placeholder = "Write a message to your league...",
  isReply = false,
  autoFocus = false,
  buttonText = "Post Message",
  disabled = false,
  allowImageUpload = false  // Only enable for full message board
}) => {
  const [messageText, setMessageText] = useState('');
  const [selectedImages, setSelectedImages] = useState([]); // File objects for preview
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]); // S3 URLs after upload
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle clipboard paste for images
  const handlePaste = async (e) => {
    if (!allowImageUpload) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if it's an image
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // Prevent default paste behavior
        const file = item.getAsFile();
        
        if (file) {
          // Validate file size (max 5MB)
          if (file.size > 5 * 1024 * 1024) {
            setError(`Pasted image too large. Maximum size is 5MB.`);
            continue;
          }
          
          imageFiles.push(file);
        }
      }
    }
    
    // Add pasted images
    if (imageFiles.length > 0) {
      const newImages = [...selectedImages, ...imageFiles].slice(0, 4);
      setSelectedImages(newImages);
      setError(null);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only JPG, PNG, GIF, and WEBP are allowed.`);
        return false;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`File too large: ${file.name}. Maximum size is 5MB.`);
        return false;
      }
      
      return true;
    });
    
    // Limit to 4 images total
    const newImages = [...selectedImages, ...validFiles].slice(0, 4);
    setSelectedImages(newImages);
    setError(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    // Also remove uploaded URL if exists
    setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrawingSave = (file) => {
    // Add drawing to selectedImages (limit to 4 total)
    const newImages = [...selectedImages, file].slice(0, 4);
    setSelectedImages(newImages);
    setShowDrawingCanvas(false);
  };

  const uploadImages = async () => {
    if (selectedImages.length === 0) {
      return [];
    }

    setIsUploadingImages(true);
    setError(null);

    try {
      const uploadPromises = selectedImages.map(async (file) => {
        const result = await leaguesAPI.uploadMessageImage(leagueId, file);
        return result.image_url;
      });

      const urls = await Promise.all(uploadPromises);
      setUploadedImageUrls(urls);
      return urls;
    } catch (err) {
      setError(`Failed to upload images: ${err.message}`);
      throw err;
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() && selectedImages.length === 0) {
      setError('Message cannot be empty');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Upload images first if any are selected
      let imageUrls = uploadedImageUrls;
      if (selectedImages.length > 0 && uploadedImageUrls.length === 0) {
        imageUrls = await uploadImages();
      }
      
      // Submit message with image URLs
      await onSubmit({
        message_text: messageText.trim(),
        image_urls: imageUrls.length > 0 ? imageUrls : null
      });
      
      // Clear form on success
      setMessageText('');
      setSelectedImages([]);
      setUploadedImageUrls([]);
    } catch (err) {
      setError(err.message || 'Failed to post message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setMessageText('');
    setSelectedImages([]);
    setUploadedImageUrls([]);
    setError(null);
    if (onCancel) onCancel();
  };

  return (
    <div className={`${dynastyTheme.components.card.base} p-4`}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Text Input with paste support */}
          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onPaste={handlePaste}
            placeholder={placeholder}
            rows={isReply ? 2 : 3}
            autoFocus={autoFocus}
            maxLength={5000}
            disabled={disabled || isSubmitting || isUploadingImages}
            className={`w-full ${dynastyTheme.components.input} resize-none`}
          />

          {/* Image Previews */}
          {selectedImages.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {selectedImages.map((file, index) => (
                <div key={index} className="relative group">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded border border-neutral-700"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className={`absolute top-1 right-1 p-1 rounded-full ${dynastyTheme.classes.bg.error} ${dynastyTheme.classes.text.white} opacity-0 group-hover:opacity-100 ${dynastyTheme.classes.transition}`}
                    disabled={isUploadingImages || isSubmitting}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {isUploadingImages && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                      <Loader className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Image Upload Button - Only show if enabled */}
              {allowImageUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={disabled || isSubmitting || isUploadingImages || selectedImages.length >= 4}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || isSubmitting || isUploadingImages || selectedImages.length >= 4}
                    className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} flex items-center gap-1`}
                    title="Add images/GIFs (max 4) or paste with Ctrl+V"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span className="text-xs">
                      {selectedImages.length > 0 ? `Images (${selectedImages.length}/4)` : 'Add Image/GIF'}
                    </span>
                  </button>
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} italic`}>
                    or paste with Ctrl+V
                  </span>
                  
                  {/* Drawing Button */}
                  <button
                    type="button"
                    onClick={() => setShowDrawingCanvas(true)}
                    disabled={disabled || isSubmitting || isUploadingImages || selectedImages.length >= 4}
                    className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} flex items-center gap-1`}
                    title="Create a drawing"
                  >
                    <Palette className="w-4 h-4" />
                    <span className="text-xs">Draw</span>
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className={`text-xs ${
                messageText.length > 4500 
                  ? dynastyTheme.classes.text.error 
                  : dynastyTheme.classes.text.neutralLight
              }`}>
                {messageText.length}/5000
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-2 rounded bg-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Upload Progress */}
          {isUploadingImages && (
            <div className={`p-2 rounded ${dynastyTheme.classes.bg.primaryLight} ${dynastyTheme.classes.text.primary} text-sm flex items-center gap-2`}>
              <Loader className="w-4 h-4 animate-spin" />
              Uploading images...
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={disabled || isSubmitting || isUploadingImages}
                className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={disabled || isSubmitting || isUploadingImages || (!messageText.trim() && selectedImages.length === 0)}
              className={`${dynastyTheme.utils.getComponent('button', 'primary', 'sm')} flex items-center gap-2`}
            >
              <Send className="w-4 h-4" />
              {isUploadingImages ? 'Uploading...' : isSubmitting ? 'Posting...' : buttonText}
            </button>
          </div>
        </div>
      </form>

      {/* Drawing Canvas Modal */}
      {showDrawingCanvas && (
        <DrawingCanvas
          onSave={handleDrawingSave}
          onCancel={() => setShowDrawingCanvas(false)}
        />
      )}
    </div>
  );
};

export default MessageComposer;
